import axios, { AxiosError } from "axios";
import { useSetAtom } from "jotai";
import { useContext } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  AuthAndPersonalDetailsFormFields,
  CustomerTypeAndSegment,
} from "@/components/Onboarding/Steps/PersonalDetails/personalDetails.types.ts";
import { handleUpdateCustomerFunction } from "@/components/Onboarding/Steps/PersonalDetails/useHandleUpdateCustomer.ts";
import { DataContext } from "@/provider/DataContextProvider.tsx";
import { createCustomer, getCustomer } from "@/services/api.ts";
import { BasicCustomerData } from "@/services/model";
import { API_ROOT, CUSTOMER_AUTHORIZATION } from "@/utils/apiUrls.ts";
import {
  accessTokenAtom,
  esimJourneyBasketIdAtom,
  refreshTokenAtom,
  showsSessionExpiredModalAtom,
} from "@/utils/atoms.ts";
import {
  hasValidPersonalDetails,
  transformPersonalDetailsFormToBasicCustomerData,
} from "@/utils/customerUtils.ts";
import { log } from "@/utils/log";
import {
  createLoginErrorHandler,
  LoginError,
} from "@/utils/loginErrorHandler.ts";

/**
 * Handles the submit of the personal details form for an anonymous user.
 * We either have to login or create an account, depending on entered form values.
 *
 * @param formValues Email, password and so on. See typings for details.
 * @param isExistingAccount Whether the user already has an account or not.
 * @param formMethods Form methods from react-hook-form.
 * @param persistCustomerAndGoToCheckout Function to persist the customer and redirect to the checkout.
 * @param handleUpdateCustomer Function to update the customer.
 * @param setHasApiError Function to set the API error state.
 */
export const useHandleAnonymousUserSubmit = (
  isExistingAccount: boolean | null,
  formMethods: UseFormReturn<AuthAndPersonalDetailsFormFields>,
  formValues: AuthAndPersonalDetailsFormFields,
  persistCustomerAndGoToCheckout: (
    customerData: BasicCustomerData,
    target: string,
  ) => void,
  handleUpdateCustomer: handleUpdateCustomerFunction,
  setHasApiError?: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  const setAccessToken = useSetAtom(accessTokenAtom);
  const setRefreshToken = useSetAtom(refreshTokenAtom);
  const setEsimJourneyBasketId = useSetAtom(esimJourneyBasketIdAtom);
  const setShowSessionExpiredModal = useSetAtom(showsSessionExpiredModalAtom);
  const { dataContext } = useContext(DataContext);
  const { t, i18n } = useTranslation();

  const baseLanguage = i18n.language.split("-")[0];

  // Create login error handler using the reusable utility
  const handleLoginError = createLoginErrorHandler(
    formMethods.setError,
    t,
    (alert) => {
      // For now, we'll use form errors instead of alerts
      formMethods.setError("password", {
        type: "backendError",
        message: alert.text,
      });
    },
    "email", // emailFieldName for this form
  );

  const loginAndPersistBasket = async () => {
    const fetchPostBody = new URLSearchParams({
      grant_type: "password",
      username: `${formValues?.email}`,
      password: `${formValues?.password}`,
    });

    try {
      const response = await axios.post<{
        access_token: string;
        refresh_token: string;
      }>(`${API_ROOT}/api-auth-server/oauth/token`, fetchPostBody, {
        headers: {
          Accept: "application/json, text/plain, */*",
          "Accept-Language": "en,de;q=0.5",
          "Content-Type": "application/x-www-form-urlencoded",
          "X-Requested-With": "XMLHttpRequest",
          Authorization: CUSTOMER_AUTHORIZATION,
          "Sec-Fetch-Dest": "empty",
          "Sec-Fetch-Mode": "cors",
          "Sec-Fetch-Site": "same-origin",
          "Sec-GPC": "1",
        },
      });

      if (!response.data.access_token || !response.data.refresh_token) {
        throw new Error("No tokens received after onboarding login");
      }

      setAccessToken(response.data.access_token);
      setRefreshToken(response.data.refresh_token);
      // Clear session expired modal state on successful onboarding login
      setShowSessionExpiredModal(undefined);

      if (!dataContext.basket?.id) {
        throw new Error("No basket id received after onboarding login");
      }

      setEsimJourneyBasketId(dataContext.basket.id);
    } catch (error) {
      log("Error when logging in an anonymous user", error);
      throw error;
    }
  };

  const handleAnonymousUser = async (
    customerTypeAndCustomerSegment: CustomerTypeAndSegment,
  ) => {
    // If we don't have an existing account and no form errors, we should create an account
    const shouldCreateAccount =
      !isExistingAccount &&
      Object.keys(formMethods.formState.errors).length === 0;

    if (shouldCreateAccount) {
      try {
        const postData = {
          ...transformPersonalDetailsFormToBasicCustomerData(
            formValues,
            {},
            baseLanguage,
            t,
          ),
          ...customerTypeAndCustomerSegment,
        };

        // Removing the phone data cause the backend can't add it on the initial create user
        const { phone, ...cleanedPostData } = postData;

        await createCustomer({
          header: { source: 1, client: 1 },
          basicCustomerData: cleanedPostData,
          authentification: {
            login: formValues?.email,
            password: formValues?.password,
          },
        });

        // Make the call to sign in
        await loginAndPersistBasket();

        // Here it is necessary to update the customer to add the phone number (cannot be done in create call...)
        // It is necessary to be signed in before the update of the customer.
        const freshCustomer = await getCustomer();

        // Update the customer to persist the phone number too.
        handleUpdateCustomer(freshCustomer, customerTypeAndCustomerSegment);
      } catch (error) {
        log("Error when creating account for anonymous user", error);

        if (error instanceof AxiosError) {
          const loginError: LoginError = {
            status: error.response?.status,
            message:
              error.response?.data?.error_description ||
              error.response?.data?.message ||
              error.message,
          };
          handleLoginError(loginError);
          // Set hasApiError to true when API error occurs
          setHasApiError?.(true);
        } else {
          formMethods.setError("password", {
            type: "backendError",
            message: t("translation:Common.generalForm.error.genericError"),
          });
          // Set hasApiError to true for non-Axios errors as well
          setHasApiError?.(true);
        }
      }
    } else {
      try {
        const customerData = await getCustomer();

        if (hasValidPersonalDetails(customerData)) {
          // Upon login, a customer who has completed the onboarding process should be redirected
          // to the 'add device' flow within the portal. A customer without a device (unfinished onboarding)
          // should be sent to the checkout of the onboarding process.
          const redirectTarget = customerData.onboardingFinished
            ? "/portal/add-device/4/checkout"
            : "/onboarding/5";
          persistCustomerAndGoToCheckout(customerData, redirectTarget);
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          const loginError: LoginError = {
            status: error.response?.status,
            message:
              error.response?.data?.error_description ||
              error.response?.data?.message ||
              error.message,
          };
          handleLoginError(loginError);
          // Set hasApiError to true when API error occurs
          setHasApiError?.(true);
        } else {
          handleLoginError({
            status: 500,
            message: (error as Error)?.message || "Unknown error",
          });
          // Set hasApiError to true for non-Axios errors as well
          setHasApiError?.(true);
        }
      }
    }
  };

  return { handleAnonymousUser };
};
